import { Box, SxProps, TextField } from "@mui/material";
import LabelHeader from "components/Label/LabelHeader";
import { ChangeEvent, useState } from "react";
import { MESSAGE } from "constants/validate";
import ZoomInImage from "components/image/ZoomInImage";
import { MstItemDetailWheelDetail } from "features/mst_wheel/types";
import { formatCurrency, formatWheelRimDiamDisplay } from "utils";

interface CellItemProps {
  label: string;
  value: number | string;
  sxValue?: SxProps;
}

interface WheelItemRowProps {
  wheel: Partial<MstItemDetailWheelDetail>;
  onRemarkChange?: (value: string) => void;
  optionsPrice?: number;
  detailData?: boolean;
}

const styles = {
  fontCell: { fontSize: { xs: "16px", md: "18px", xl: "20px" } },
};

/**
 * CellItem component displays an information cell with a label and value
 * @param {CellItemProps} props - Component props
 * @param {string} props.label - Label displayed on the left side
 * @param {string} props.value - Value displayed on the right side
 * @returns {JSX.Element} CellItem component
 */
function CellItem({ label, value, sxValue }: CellItemProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: { xs: "calc(100%/3)", md: "calc(100%/4)", xl: "calc(100%/5)" },
        height: 48,
      }}
    >
      <LabelHeader
        label={label}
        sxBox={{ justifyContent: "center", width: "52%" }}
        sxTypography={{ ml: 0, ...styles.fontCell }}
      />
      <Box
        title={String(value)}
        sx={{
          // display: "flex",
          justifyContent: "center",
          width: "48%",
          display: "-webkit-box",
          textAlign: "center",
          WebkitLineClamp: 1,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...styles.fontCell,
          ...sxValue,
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

/**
 * WheelItemRow component displays detailed information about the wheel including image and technical specifications
 * @param {WheelItemRowProps} props - Component props
 * @param {MstItemDetailWheelDetail} props.wheel - Wheel data
 * @param {function} props.onRemarkChange - Callback function to handle remark change
 * @returns {JSX.Element} WheelItemRow component
 */
export default function WheelItemRow({
  wheel,
  onRemarkChange,
  optionsPrice,
  detailData = false,
}: WheelItemRowProps) {
  const [visibleZoomIn, setVisibleZoomIn] = useState(false);
  const [remark, setRemark] = useState(wheel.remarks || "");
  const [error, setError] = useState("");
  const maxLength = 100;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setRemark(inputValue);

    if (inputValue.length > maxLength) {
      setError(MESSAGE.MAX_LENGTH(maxLength));
    } else {
      setError("");
    }

    if (onRemarkChange) {
      onRemarkChange(inputValue);
    }
  };

  // Calculate discount rate (special_discount if available, otherwise fixed_discount)
  const discountRate = wheel.fixed_discount || 0;

  console.log("wheel.midw_wheel_hub_diam", wheel, wheel.midw_wheel_hub_diam);

  const wheelInfo = [
    { id: 68, label: "ホイールメーカー", value: wheel.maker_name || "" },
    {
      id: 72,
      label: "サイズ/ハブ径",
      value: `${wheel.wheel_size} ${formatWheelRimDiamDisplay(wheel.midw_wheel_hub_diam || wheel.wheel_hub_diam)}Φ `,
    },
    { id: 76, label: "本数", value: "4本" },
    { id: 80, label: "特別割引率", value: `${discountRate}%` },
    { id: 84, label: "オプション合計", value: formatCurrency(optionsPrice || 0) },
    { id: 70, label: "ホイールブランド", value: wheel.brand_name || "" },
    { id: 74, label: "カラー", value: wheel.wheel_color_name || "" },
    { id: 78, label: "通常4本価格", value: formatCurrency(wheel.regular_price || 0) },
    { id: 82, label: "割引後4本価格", value: formatCurrency(wheel.discounted_price || 0) },
    { id: 86, label: "ホイール4本合計", value: formatCurrency(wheel.final_price || 0) },
  ];

  return (
    <>
      <Box sx={{ display: "flex", minWidth: "1600px" }}>
        <Box
          sx={{
            width: 175,
            minWidth: 175,
            maxHeight: 150,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <img
            src={wheel.wheel_image_url || "/img/wheel/wheel.svg"}
            alt="Wheel"
            style={{
              width: "100%",
              height: "100%",
              display: "block",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 5,
              right: 5,
              borderRadius: "50%",
              cursor: "pointer",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setVisibleZoomIn(true)}
          >
            <Box
              component="span"
              sx={{
                fontSize: "16px",
                color: "var(--gray-550)",
              }}
            >
              <img src="/img/icons/zoom-in.svg" alt="zoom in icon" />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", flex: 1, mb: error ? 3 : 0 }}>
          {wheelInfo.map((el, index) => (
            <CellItem
              label={el.label}
              value={el.value}
              key={el.id}
              sxValue={
                wheelInfo.length - 1 === index
                  ? {
                      fontSize: { sm: "18px", md: "20px", xl: "25px" },
                      fontWeight: "bold",
                    }
                  : {}
              }
            />
          ))}
          <Box sx={{ display: "flex", flex: 1, alignItems: "center", height: 48, width: "100%" }}>
            <LabelHeader
              label="備考"
              sxBox={{
                justifyContent: "center",
                width: { xs: "calc(78%/3)", md: "calc(104%/4)", xl: "calc(52%/5)" },
              }}
              sxTypography={{ ml: 0, ...styles.fontCell }}
            />
            <Box
              sx={{
                flex: 1,
                display: "flex",
                maxHeight: 48,
                px: 1,
                fontSize: { xs: "16px", md: "18px", xl: "20px" },
              }}
            >
              {detailData ? (
                remark
              ) : (
                <TextField
                  size="medium"
                  sx={{
                    mx: "2px",
                    boxSizing: "border-box",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                    },
                    "& .MuiInputBase-input": {
                      py: "0",
                      height: 48,
                      ...styles.fontCell,
                    },
                  }}
                  FormHelperTextProps={{
                    sx: {
                      color: "var(--red-800) !important",
                      fontSize: { xs: "16px", md: "18px", xl: "20px" },
                      ml: 0,
                      mt: 0,
                    },
                  }}
                  onChange={handleChange}
                  error={!!error}
                  helperText={error}
                  fullWidth
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <ZoomInImage
        visibleModal={visibleZoomIn}
        imageUrl={wheel.wheel_image_url || "/img/wheel/wheel.svg"}
        alt="temp image"
        setVisibleModal={setVisibleZoomIn}
      />
    </>
  );
}
